import { useState } from 'react';

interface AskDrawerProps {
  onClose: () => void;
  brand: string;
}

/**
 * Frontend stub for "Ask SFP" — wires to a Cloud Function `askSfp` (not yet
 * implemented). Until the function exists, posts the question to a local
 * placeholder endpoint and surfaces the response or a friendly stub.
 */
export function AskDrawer({ onClose, brand }: AskDrawerProps) {
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState<Array<{ q: string; a: string }>>([]);
  const [pending, setPending] = useState(false);

  const ask = async () => {
    if (!question.trim()) return;
    const q = question.trim();
    setQuestion('');
    setPending(true);
    setHistory((h) => [...h, { q, a: '' }]);
    try {
      // Placeholder — when the Cloud Function ships, swap to httpsCallable('askSfp').
      const a = `(${brand} Q&A isn't wired up yet — this is a placeholder. Your question was: "${q}". When the askSfp Cloud Function lands, it'll use Claude over your indexed docs.)`;
      await new Promise((r) => setTimeout(r, 400));
      setHistory((h) => {
        const copy = h.slice();
        copy[copy.length - 1] = { q, a };
        return copy;
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-[480px] flex-col border-l border-stone-200 bg-white shadow-2xl">
      <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-stone-800">Ask {brand}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400">
            Powered by Claude over your indexed documents
          </div>
        </div>
        <button onClick={onClose} className="rounded bg-stone-900 px-3 py-1 text-xs text-white" type="button">
          Close
        </button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto bg-stone-50 p-4">
        {history.length === 0 && (
          <div className="rounded-lg border border-dashed border-stone-300 p-4 text-xs text-stone-500">
            Try: <em>"What does Topco3's op agreement say about distributions?"</em> or{' '}
            <em>"Show me my Con Ed bills from last quarter."</em>
          </div>
        )}
        {history.map((turn, i) => (
          <div key={i} className="space-y-2">
            <div className="rounded-lg bg-[color:var(--sfp-primary)] px-3 py-2 text-xs text-white">{turn.q}</div>
            {turn.a ? (
              <div className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs text-stone-700">
                {turn.a}
              </div>
            ) : (
              <div className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs text-stone-400 italic">
                Thinking…
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="border-t border-stone-200 bg-white p-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void ask();
          }}
        >
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={`Ask ${brand} anything about your documents…`}
            rows={3}
            disabled={pending}
            className="w-full resize-none rounded border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void ask();
              }
            }}
          />
          <div className="mt-2 flex items-center justify-between">
            <div className="text-[10px] text-stone-400">⌘+Enter to send</div>
            <button
              type="submit"
              disabled={pending || !question.trim()}
              className="rounded bg-[color:var(--sfp-primary)] px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
            >
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
